export interface BrowserFlushable {
  forceFlush(): Promise<void>;
}

/**
 * Drain browser telemetry while the page still has a lifecycle budget. OTLP
 * exporters use fetch keepalive in the browser; starting the flush on hide or
 * pagehide lets those bounded requests finish after navigation.
 */
export function installBrowserFlushOnHide(
  flushables: BrowserFlushable[],
): () => void {
  let pending: Promise<void> | undefined;

  const flush = (): void => {
    if (pending) {
      return;
    }
    pending = (async () => {
      // Browsers cap the total amount of in-flight keepalive data per page.
      // Drain providers sequentially so logs and traces do not compete for
      // that budget during the same pagehide transition.
      for (const flushable of flushables) {
        try {
          await flushable.forceFlush();
        } catch {
          // Telemetry cleanup must never block navigation.
        }
      }
    })().finally(() => {
      pending = undefined;
    });
  };

  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pagehide", flush);

  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("pagehide", flush);
  };
}
